import { useUserStates } from '@/features/user/stores/user.store';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { useSelectedCases, useSelectedMenuCaseMode, useCasesActions } from '../../../stores/cases.store';
import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import {
  ITeamWithUsersInUserToAssignCase,
  IUserInUserToAssignCase,
  ITeamInUserToAssignCase,
  useGetUsersToAssignCaseQuery,
} from '../../../api/get-users-to-assign-case.api';
import { memo, useCallback, useMemo, useState } from 'react';
import { useAssignCases } from '../../../api/assign-cases.api';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Search } from 'lucide-react';
import Input from '@/components/ui/input';
import { cn } from '@/utils/cn';
// import { LoadingSpinner } from '@/components/ui/loading-spinner';
import useSelectedCaseQs from '../../../hooks/use-case-qs';
import { Spinner } from '@/components/_ui/spinner';

const DEFAULT_NO_TEAM_DATA = {
  teamId: null,
  name: 'No team',
  users: [],
};

export type TAssignCaseToMeModalType = 'AssignCaseToMeModal';
export type IAssignCaseToMeModalProps = {
  caseId?: string | null; // Optional: If provided, only assign to this conversation
};

const AssignCaseToMeModal = memo(({ caseId }: IAssignCaseToMeModalProps) => {
  // HOOKS
  const { handleSelectedCase } = useSelectedCaseQs();
  const showToast = useCustomToast();
  const { user: userLogged } = useUserStates();
  const selectedCases = useSelectedCases();
  const selectedCaseMode = useSelectedMenuCaseMode();
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const { setResetSelectedCases, setResetSelectedMenuCaseMode, setResetSelectedCase } = useCasesActions();

  const isOpenAssignCaseToMeModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('AssignCaseToMeModal');
  }, [caseModuleModalRootActions]);

  const { data: usersToAssignCase, isLoading: _isLoadingUsersToAssignCase } = useGetUsersToAssignCaseQuery({
    queryConfig: {
      enabled: !!isOpenAssignCaseToMeModal,
    },
  });
  const { users } = useMemo(() => {
    return {
      users: usersToAssignCase?.payload?.users || [],
    };
  }, [usersToAssignCase]);

  const [searchTeam, setSearchTeam] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<ITeamWithUsersInUserToAssignCase | undefined>(DEFAULT_NO_TEAM_DATA);

  const mutationAssignCasesToMe = useAssignCases({
    mutationConfig: {
      onSuccess: () => {
        showToast({
          type: 'success',
          message: 'Assign cases to me successfully',
        });
        caseModuleModalRootActions.closeModal('AssignCaseToMeModal');
        handleSelectedCase(null);
        setSelectedTeam(undefined);
        setResetSelectedCases();
        setResetSelectedCase();
        if (selectedCaseMode !== 'idle') {
          setResetSelectedMenuCaseMode();
        }
      },
    },
  });

  // EFFECTS
  // --

  // HELPERS
  const isDisabledButton = useMemo(() => {
    return !selectedTeam;
  }, [selectedTeam]);

  const filteredTeamsByLoggedUser = useMemo(() => {
    const result = users.find((user: IUserInUserToAssignCase) => user.userId === userLogged?.userId);
    return [
      DEFAULT_NO_TEAM_DATA,
      ...(result?.teams?.filter((team: ITeamInUserToAssignCase) =>
        team?.name?.toLowerCase().includes(searchTeam?.toLowerCase())
      ) || []),
    ];
  }, [users, userLogged, searchTeam]);

  // EVENT HANDLERS
  const handleCloseModal = useCallback(() => {
    caseModuleModalRootActions.closeModal('AssignCaseToMeModal');
    if (selectedCaseMode === 'idle') {
      setResetSelectedCases();
    }
  }, [caseModuleModalRootActions, selectedCaseMode, setResetSelectedCases]);

  const handleSubmit = useCallback(() => {
    const caseIdsFromStore = selectedCases?.map((item) => item?.caseId) || [];
    const caseIds = caseId ? [caseId] : caseIdsFromStore;

    if (caseIds?.length === 0) {
      showToast({
        type: 'error',
        message: 'Please select at least one case',
      });
      return;
    }

    if (!selectedTeam) {
      showToast({
        type: 'error',
        message: 'Please select team or to no team',
      });
      return;
    }

    mutationAssignCasesToMe.mutate({
      caseIds,
      userId: userLogged?.userId || '',
      teamId: selectedTeam?.teamId || null,
    });
  }, [caseId, selectedCases, selectedTeam, userLogged, showToast, mutationAssignCasesToMe]);

  // EARLY RETURN
  if (!isOpenAssignCaseToMeModal) {
    return null;
  }

  // RENDER LOGIC
  return (
    <Modal
      isCloseOutside={false}
      isButtonClose={true}
      onClose={handleCloseModal}
      className="w-[500px] p-[24px]"
      isOpen={isOpenAssignCaseToMeModal}
    >
      <ModalHeader>
        <ModalTitle>Assign To Me</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="grid gap-[16px]">
          <div className="flex-1 h-full flex flex-col">
            <div>
              <label htmlFor="" className="text-[#9D9D9C] text-[12px] font-normal leading-[12px]">
                Select Team
              </label>
              <div className="relative">
                <Search className="absolute top-[50%] left-[8px] translate-y-[-50%] text-[#D9D9D9] w-[16px] h-[16px]" />
                <Input
                  placeholder="Search team"
                  className="w-full pl-[30px]"
                  value={searchTeam}
                  onChange={(e) => setSearchTeam(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-[8px] flex-1 overflow-y-auto py-[8px]">
              {filteredTeamsByLoggedUser?.length > 0 ? (
                filteredTeamsByLoggedUser?.map((team, index) => {
                  const isSelected = selectedTeam?.teamId === team.teamId;

                  return (
                    <label
                      key={index + 1}
                      className={cn(
                        'flex flex-row items-center gap-[8px]  px-[16px] text-[#25151E] py-[8px] rounded-[8px] cursor-pointer group'
                      )}
                    >
                      <input
                        type="radio"
                        name="selectedTeam"
                        value={team.teamId || ''}
                        checked={isSelected}
                        onChange={() => {
                          setSelectedTeam(team as ITeamWithUsersInUserToAssignCase);
                        }}
                        className="sr-only"
                      />
                      <div
                        className={cn(
                          'w-[16px] h-[16px] rounded-full border flex items-center justify-center p-[2px]',
                          {
                            'border-[#0C8DBF] ': isSelected,
                            'border-[#D9D9D9]': !isSelected,
                          }
                        )}
                      >
                        {isSelected && <div className="w-full h-full rounded-full bg-[#0C8DBF]"></div>}
                      </div>
                      <span className={cn('text-[12px] font-normal text-[#25151E] ')}>{team.name}</span>
                    </label>
                  );
                })
              ) : (
                <div className="flex flex-col gap-[8px] flex-1 overflow-y-auto py-[8px]">
                  <span className="text-[#9D9D9C] text-[12px] font-normal leading-[12px]">No team found</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalFooter className="gap-[12px]">
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleCloseModal();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton onClick={handleSubmit} disabled={isDisabledButton}>
          {mutationAssignCasesToMe.isPending ? <Spinner className="w-[18px] h-[18px]" /> : null} Assign
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
AssignCaseToMeModal.displayName = 'AssignCaseToMeModal';

export default AssignCaseToMeModal;
